import { useState, useEffect, useContext, useMemo } from "react";
import {
    Table,
    Card,
    Tag,
    Space,
    Typography,
    Button,
    Modal,
    Input,
    Select,
    Row,
    Col,
    DatePicker,
} from "antd";
import { EyeOutlined, CheckOutlined, CloseOutlined, FilterOutlined, ClearOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function ShowSpecialPermission() {
    const { authData, backUrl } = useContext(AuthContext);

    const [permissions, setPermissions] = useState([]);
    const [filteredPermissions, setFilteredPermissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const [approveModalVisible, setApproveModalVisible] = useState(false);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [selectedPermission, setSelectedPermission] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");

    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [workLocations, setWorkLocations] = useState([]);

    // Filter states
    const [filters, setFilters] = useState({
        status: null,
        gender: null,
        officeType: null,
        dateRange: null,
        organisation: null,
        department: null,
        createdBy: null,
    });

    /* ==============================
       FETCH REFERENCE DATA
    ============================== */
    const fetchReferenceData = async () => {
        try {
            const [orgsRes, deptsRes, desRes, locRes] = await Promise.all([
                axios.get(`${backUrl}/api/emp/organisations/`),
                axios.get(`${backUrl}/api/emp/departments/`),
                axios.get(`${backUrl}/api/emp/designations/`),
                axios.get(`${backUrl}/api/emp/worklocation/`),
            ]);

            setOrganizations(orgsRes.data?.results || []);
            setDepartments(deptsRes.data?.results || []);
            setDesignations(desRes.data?.results || []);
            setWorkLocations(locRes.data?.results || []);
        } catch (err) {
            console.error("Failed to load reference data", err);
            toast.error("Failed to load reference data");
        }
    };

    /* ==============================
       FETCH PERMISSIONS
    ============================== */
    const fetchPermissions = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/permission-considerations/?page=${page}&page_size=${pageSize}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            const data = res.data?.results || [];
            setPermissions(data);
            setFilteredPermissions(data);
            setPagination({
                current: page,
                pageSize,
                total: res.data?.count || 0,
            });
        } catch (err) {
            console.error("Failed to load permissions", err);
            toast.error("Failed to load permissions");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReferenceData();
        fetchPermissions();
    }, []);

    /* ==============================
       APPLY FILTERS
    ============================== */
    useEffect(() => {
        let filtered = [...permissions];

        // Status filter
        if (filters.status) {
            filtered = filtered.filter(p => p.status === filters.status);
        }

        // Gender filter
        if (filters.gender) {
            filtered = filtered.filter(p => p.gender === filters.gender);
        }

        // Office Type filter
        if (filters.officeType) {
            filtered = filtered.filter(p => {
                const isSite = Array.isArray(p.work_locations) && p.work_locations.length > 0;
                return filters.officeType === "Site" ? isSite : !isSite;
            });
        }

        // Date Range filter
        if (filters.dateRange && filters.dateRange.length === 2) {
            filtered = filtered.filter(p => {
                const date = dayjs(p.date);
                return date.isAfter(filters.dateRange[0].startOf('day')) &&
                    date.isBefore(filters.dateRange[1].endOf('day'));
            });
        }

        // Organisation filter
        if (filters.organisation) {
            filtered = filtered.filter(p =>
                Array.isArray(p.organisation_names) && p.organisation_names.includes(filters.organisation)
            );
        }

        // Department filter
        if (filters.department) {
            filtered = filtered.filter(p =>
                Array.isArray(p.department_names) && p.department_names.includes(filters.department)
            );
        }

        // Created By filter
        if (filters.createdBy) {
            filtered = filtered.filter(p =>
                p.created_by_name?.toLowerCase().includes(filters.createdBy.toLowerCase())
            );
        }

        setFilteredPermissions(filtered);
    }, [filters, permissions]);

    /* ==============================
       CLEAR FILTERS
    ============================== */
    const clearFilters = () => {
        setFilters({
            status: null,
            gender: null,
            officeType: null,
            dateRange: null,
            organisation: null,
            department: null,
            createdBy: null,
        });
    };

    /* ==============================
       GET UNIQUE VALUES FOR FILTERS
    ============================== */
    const uniqueOrganisations = useMemo(() => {
        const orgs = new Set();
        permissions.forEach(p => {
            if (Array.isArray(p.organisation_names)) {
                p.organisation_names.forEach(name => orgs.add(name));
            }
        });
        return Array.from(orgs).sort();
    }, [permissions]);

    const uniqueDepartments = useMemo(() => {
        const depts = new Set();
        permissions.forEach(p => {
            if (Array.isArray(p.department_names)) {
                p.department_names.forEach(name => depts.add(name));
            }
        });
        return Array.from(depts).sort();
    }, [permissions]);

    /* ==============================
       TABLE COLUMNS
    ============================== */
    const columns = useMemo(
        () =>
            [
                {
                    title: "Date",
                    dataIndex: "date",
                    key: "date",
                    width: 120,
                    render: (date) => dayjs(date).format("DD MMM YYYY"),
                    sorter: (a, b) =>
                        dayjs(a.date).unix() - dayjs(b.date).unix(),
                },
                {
                    title: "Organisation",
                    dataIndex: "organisation_names",
                    key: "organisation_names",
                    width: 200,
                    render: (names) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {names && names.length > 0 ? (
                                names.map((name, idx) => (
                                    <p>
                                        {name}
                                    </p>
                                ))
                            ) : (
                                <p>All Organisations</p>
                            )}
                        </div>
                    ),
                },
                {
                    title: "Department",
                    dataIndex: "department_names",
                    key: "department_names",
                    width: 220,
                    render: (names) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {names && names.length > 0 ? (
                                names.map((name, idx) => (
                                    <p>
                                        {name}
                                    </p>
                                ))
                            ) : (
                                <p>All Departments</p>
                            )}
                        </div>
                    ),
                },
                {
                    title: "Start Time",
                    dataIndex: "new_start_time",
                    key: "new_start_time",
                    width: 120,
                    render: (time) => {
                        if (!time) return "-";
                        const parsed = dayjs(time, ["HH:mm:ss", "HH:mm"], true);
                        return parsed.isValid() ? parsed.format("hh:mm A") : "-";
                    },
                },
                {
                    title: "End Time",
                    dataIndex: "new_end_time",
                    key: "new_end_time",
                    width: 120,
                    render: (time) => {
                        if (!time) return "-";
                        const parsed = dayjs(time, ["HH:mm:ss", "HH:mm"], true);
                        return parsed.isValid() ? parsed.format("hh:mm A") : "-";
                    },
                },
                {
                    title: "Applicable For",
                    dataIndex: "gender",
                    key: "gender",
                    width: 100,
                    render: (gender) => (
                        <Tag
                            color={
                                gender === "Male"
                                    ? "blue"
                                    : gender === "Female"
                                        ? "pink"
                                        : "default"
                            }
                        >
                            {gender || "All"}
                        </Tag>
                    ),
                },
                {
                    title: "Office Type",
                    key: "office_type",
                    width: 120,
                    render: (_, record) => {
                        const isSite =
                            Array.isArray(record.work_locations) &&
                            record.work_locations.length > 0;
                        return (
                            <p color={isSite ? "orange" : "green"}>
                                {isSite ? "Site Office" : "Head Office"}
                            </p>
                        );
                    },
                },
                {
                    title: "Reason",
                    dataIndex: "reason",
                    key: "reason",
                    width: 140,
                },
                {
                    title: "Created By",
                    dataIndex: "created_by_name",
                    key: "created_by_name",
                    width: 140,
                },
                {
                    title: "Status",
                    dataIndex: "status",
                    key: "status",
                    width: 110,
                    render: (status) => (
                        <Tag
                            color={
                                status === "Approved"
                                    ? "success"
                                    : status === "Rejected"
                                        ? "error"
                                        : "warning"
                            }
                        >
                            {status}
                        </Tag>
                    ),
                },
                {
                    title: "Created At",
                    dataIndex: "created_at",
                    key: "created_at",
                    width: 170,
                    render: (date) =>
                        dayjs(date).format("DD MMM YYYY hh:mm A"),
                    sorter: (a, b) =>
                        dayjs(a.created_at).unix() -
                        dayjs(b.created_at).unix(),
                },
                authData?.employee?.is_superuser && {
                    title: "Actions",
                    key: "actions",
                    width: 180,
                    render: (_, record) => (
                        <Space size="small">
                            {record.status === "Pending" ? (
                                <>
                                    <Button
                                        type="link"
                                        icon={<CheckOutlined />}
                                        style={{ color: "#52c41a" }}
                                        onClick={() => {
                                            setSelectedPermission(record);
                                            setApproveModalVisible(true);
                                        }}
                                    >
                                        Approve
                                    </Button>
                                    <Button
                                        type="link"
                                        danger
                                        icon={<CloseOutlined />}
                                        onClick={() => {
                                            setSelectedPermission(record);
                                            setRejectModalVisible(true);
                                        }}
                                    >
                                        Reject
                                    </Button>
                                </>
                            ) : (
                                <Text type="secondary">{record.status}</Text>
                            )}
                        </Space>
                    ),
                },
            ].filter(Boolean),
        [authData]
    );

    /* ==============================
       TABLE CHANGE
    ============================== */
    const handleTableChange = (pager) => {
        setPagination({
            ...pagination,
            current: pager.current,
            pageSize: pager.pageSize,
        });
    };

    /* ==============================
       APPROVE / REJECT
    ============================== */
    const handleApprove = async () => {
        try {
            await axios.post(
                `${backUrl}/api/hr/permission-considerations/${selectedPermission.id}/approve/`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("Permission approved successfully!");
            setApproveModalVisible(false);
            fetchPermissions(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Approval failed", err);
            toast.error(err.response?.data?.message || "Failed to approve permission");
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            toast.error("Rejection reason is required");
            return;
        }
        try {
            await axios.patch(
                `${backUrl}/api/hr/permission-considerations/${selectedPermission.id}/`,
                {
                    status: "Rejected",
                    rejection_reason: rejectionReason,
                },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("Permission rejected successfully");
            setRejectModalVisible(false);
            setRejectionReason("");
            fetchPermissions(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Rejection failed", err);
            toast.error(err.response?.data?.message || "Failed to reject permission");
        }
    };

    /* ==============================
       RENDER
    ============================== */
    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Filters Card */}
            <Card
                style={{ marginBottom: 16 }}
                title={
                    <Space>
                        <FilterOutlined />
                    </Space>
                }

            >
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Status</Text>
                        <Select
                            placeholder="Select Status"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            value={filters.status}
                            onChange={(value) => setFilters({ ...filters, status: value })}
                        >
                            <Select.Option value="Pending">Pending</Select.Option>
                            <Select.Option value="Approved">Approved</Select.Option>
                            <Select.Option value="Rejected">Rejected</Select.Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Gender</Text>
                        <Select
                            placeholder="Select Gender"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            value={filters.gender}
                            onChange={(value) => setFilters({ ...filters, gender: value })}
                        >
                            <Select.Option value="Male">Male</Select.Option>
                            <Select.Option value="Female">Female</Select.Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Office Type</Text>
                        <Select
                            placeholder="Select Office Type"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            value={filters.officeType}
                            onChange={(value) => setFilters({ ...filters, officeType: value })}
                        >
                            <Select.Option value="Head">Head Office</Select.Option>
                            <Select.Option value="Site">Site Office</Select.Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Organisation</Text>
                        <Select
                            placeholder="Select Organisation"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            showSearch
                            value={filters.organisation}
                            onChange={(value) => setFilters({ ...filters, organisation: value })}
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {uniqueOrganisations.map(org => (
                                <Select.Option key={org} value={org}>{org}</Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Department</Text>
                        <Select
                            placeholder="Select Department"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            showSearch
                            value={filters.department}
                            onChange={(value) => setFilters({ ...filters, department: value })}
                            filterOption={(input, option) =>
                                (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {uniqueDepartments.map(dept => (
                                <Select.Option key={dept} value={dept}>{dept}</Select.Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Date Range</Text>
                        <RangePicker
                            style={{ width: '100%', marginTop: 4 }}
                            value={filters.dateRange}
                            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
                            format="DD MMM YYYY"
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <Text strong>Created By</Text>
                        <Input
                            placeholder="Search by name"
                            style={{ width: '100%', marginTop: 4 }}
                            allowClear
                            value={filters.createdBy}
                            onChange={(e) => setFilters({ ...filters, createdBy: e.target.value })}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={filteredPermissions}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    total: filteredPermissions.length,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
                bordered
                size="small"
                scroll={{ x: 2200 }}
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{ backgroundColor: "#0e497b", color: 'white' }}>
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />

            {/* Approve Modal */}
            <Modal
                title="Approve Permission"
                open={approveModalVisible}
                onOk={handleApprove}
                onCancel={() => setApproveModalVisible(false)}
                okText="Approve"
                okButtonProps={{ style: { backgroundColor: "#52c41a" } }}
            >
                <p>Are you sure you want to approve this permission request?</p>
                {selectedPermission && (
                    <div style={{ marginTop: 16 }}>
                        <Text strong>Date:</Text> {dayjs(selectedPermission.date).format("DD MMM YYYY")}<br />
                        <Text strong>Created By:</Text> {selectedPermission.created_by_name}<br />
                        {selectedPermission.organisation_names && selectedPermission.organisation_names.length > 0 && (
                            <>
                                <Text strong>Organisations:</Text> {selectedPermission.organisation_names.join(", ")}<br />
                            </>
                        )}
                        {selectedPermission.department_names && selectedPermission.department_names.length > 0 && (
                            <>
                                <Text strong>Departments:</Text> {selectedPermission.department_names.join(", ")}
                            </>
                        )}
                    </div>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal
                title="Reject Permission"
                open={rejectModalVisible}
                onOk={handleReject}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setRejectionReason("");
                }}
                okText="Reject"
                okButtonProps={{ danger: true }}
            >
                <p>Please provide a reason for rejection:</p>
                {selectedPermission && (
                    <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                        <Text strong>Date:</Text> {dayjs(selectedPermission.date).format("DD MMM YYYY")}<br />
                        <Text strong>Created By:</Text> {selectedPermission.created_by_name}
                    </div>
                )}
                <TextArea
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter rejection reason..."
                />
            </Modal>
        </>
    );
}